import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";

import {
  DogPage,
  DogFactsBar,
  DogProfileActions
} from "components/Dogs/DogComponents.js";
import { LitterListingGrid } from "components/Litters/LitterComponents.js";
import SEO from "components/SEO/SEO.js";
import DogGallery from "./DogGallery";
import { ArrowLeft } from "lucide-react";

const barnHuntIcon = require("../../assets/img/icon-barnhunt.png");
const dockDivingIcon = require("../../assets/img/icon-dockdiving.png");
const fastCatIcon = require("../../assets/img/icon-fastcat.png");
const flyballIcon = require("../../assets/img/icon-flyball.png");
const herdingIcon = require("../../assets/img/icon-herding.png");
const stackImage = require("../../assets/img/dogs/venus/venus-stack.jpg");
const pedigreeImage = require("../../assets/img/dogs/venus/pedigree.jpg");

const clearances = [
  {
    id: "venus-pennhip-2021",
    label: "PennHIP Report",
    shortLabel: "PennHIP",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "March 2021",
    image: require("../../assets/img/dogs/venus/clearance1.jpeg"),
    alt: "Venus PennHIP report from March 2021"
  },
  {
    id: "venus-ofa-shoulders-2021",
    label: "OFA Shoulder Evaluation",
    shortLabel: "Shoulders",
    category: "Orthopedic",
    detailLabel: "Shoulders",
    date: "June 2021",
    image: require("../../assets/img/dogs/venus/clearance2.png"),
    alt: "Venus OFA shoulder evaluation from June 2021"
  },
  {
    id: "venus-ofa-elbows-2021",
    label: "OFA Prelim Elbow Evaluation",
    shortLabel: "Elbows",
    category: "Orthopedic",
    detailLabel: "Elbows",
    date: "June 2021",
    image: require("../../assets/img/dogs/venus/clearance3.png"),
    alt: "Venus OFA elbow evaluation from June 2021"
  },
  {
    id: "venus-embark-dna-2021",
    label: "Embark DNA Test Report",
    shortLabel: "Embark DNA",
    category: "Genetic",
    detailLabel: "DNA Panel",
    date: "March 2021",
    image: require("../../assets/img/dogs/venus/clearance4.jpg"),
    alt: "Venus Embark DNA test report from March 2021"
  },
  {
    id: "venus-genoscoper-dna-2021",
    label: "Mother and Father DNA",
    shortLabel: "Parents DNA",
    category: "Genetic",
    detailLabel: "Parents DNA",
    image: require("../../assets/img/dogs/venus/clearance5.jpg"),
    alt: "Venus Genoscoper DNA comparison report"
  },
  {
    id: "venus-ofa-hips-2021",
    label: "OFA Prelim Hip Evaluation",
    shortLabel: "OFA Hips",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "June 2021",
    image: require("../../assets/img/dogs/venus/clearance6.jpg"),
    alt: "Venus OFA hip evaluation from June 2021"
  },
  {
    id: "venus-ofa-eyes-2022",
    label: "CAER Eye Certification",
    shortLabel: "CAER 2022",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "November 2022",
    image: require("../../assets/img/dogs/venus/clearance7.jpg"),
    alt: "Venus OFA eye certification from November 2022"
  },
  {
    id: "venus-ofa-cardiac-2022",
    label: "OFA Advanced Cardiac Clearance",
    shortLabel: "Cardiac",
    category: "Cardiac",
    date: "November 2022",
    image: require("../../assets/img/dogs/venus/clearance8.jpg"),
    alt: "Venus OFA advanced cardiac clearance from November 2022"
  },
  {
    id: "venus-ofa-eyes-2025",
    label: "CAER Eye Certification",
    shortLabel: "CAER 2025",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "January 2025",
    image: require("../../assets/img/dogs/venus/clearance9.jpg"),
    alt: "Venus CAER eye certification from January 2025"
  }
];

const galleryImages = [
  require("../../assets/img/dogs/venus/reg1.jpg"),
  require("../../assets/img/dogs/venus/reg2.jpg"),
  require("../../assets/img/dogs/venus/reg3.jpg"),
  require("../../assets/img/dogs/venus/reg4.jpg"),
  require("../../assets/img/dogs/venus/reg5.jpg"),
  require("../../assets/img/dogs/venus/reg6.jpg"),
  require("../../assets/img/dogs/venus/reg7.jpg"),
  require("../../assets/img/dogs/venus/reg8.jpg"),
  require("../../assets/img/dogs/venus/reg9.jpg"),
  require("../../assets/img/dogs/venus/reg10.jpg"),
  require("../../assets/img/dogs/venus/reg11.jpg"),
  require("../../assets/img/dogs/venus/reg12.jpg"),
  require("../../assets/img/dogs/venus/reg13.jpg"),
  require("../../assets/img/dogs/venus/reg14.jpg"),
  require("../../assets/img/dogs/venus/reg15.jpg"),
  require("../../assets/img/dogs/venus/reg16.jpg"),
  require("../../assets/img/dogs/venus/reg17.jpg"),
  require("../../assets/img/dogs/venus/reg18.jpg"),
  require("../../assets/img/dogs/venus/reg19.jpg"),
  require("../../assets/img/dogs/venus/reg20.jpg"),
  require("../../assets/img/dogs/venus/reg21.jpg"),
  require("../../assets/img/dogs/venus/reg22.jpg"),
  require("../../assets/img/dogs/venus/reg23.jpg"),
  require("../../assets/img/dogs/venus/reg24.jpg")
];

const venusProfile = {
  name: "Venus",
  registeredName: "Rafter W Venus ONYX DM HSAS HSBS BCAT RATN",
  about: "Venus is a black and white smooth coat border collie who was bred by Cyndy Douan at Rafter W in Georgia. She was purchased at 15 months from her previous owner. She came started on sheep and trials in USBCHA and AKC herding. She is very friendly and affectionate. She came into a house full of dogs with ease and has won everyone over since. She is AKC and ABCA registered.",
  facts: [
    { label: "Breed", value: "Border Collie" },
    { label: "Born", value: "11/12/19" },
    { label: "Height", value: '18"' },
    { label: "Weight", value: "30lbs" }
  ],
  temperament: 'Venus has the ideal tempermant that I seek out in a border collie. She is very much a "boy dog" goofy personality with the high drive and serious work ethic that typically comes with females. She is friendly with everyone and loves cuddling. She is biddable and always willing to do what is asked of her. She handles busy, chaotic environments without batting an eye and is very enthusiastic to work. She can work for hours but is equally happy being belly-up on the couch all day. She works for food and toys equally despite not having any play foundations as a pup.',
  athleticAbility: "V has amazing potential in herding and dock diving and is currently competing in flyball. She took high in trial and high combined her first AKC trial. She is competing in the pro novice level in USBCHA. Her first dock diving event was a pb of 23ft and hshe currently jumps in the 26ft range. She debuted in flyball March 2022 and ran 3.8 - 3.9s her first tournament with a personal best that weekend of 3.805 and her first race being 3.877.",
  video: {
    src: "https://www.youtube.com/embed/videoseries?si=GV-kfEHFHNrsgREr&amp;list=PLta6k7s4hvnkVVH_22PYMxIzuBfPVVuWf"
  }
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "OFA Good*, PennHip .32/.35" },
      { label: "Elbows", value: "Normal*" },
      { label: "Shoulders", value: "OFA Normal" },
      { label: "Spine", value: "LTV Free" },
      { label: "Eyes", value: "Clear w/ PSTO (2022, 2025)" },
      { label: "Heart", value: "OFA Advanced Cardiac Clear" },
      { label: "CHIC #", value: "178944" }
    ],
    note: "* OFA Prelim"
  },
  {
    title: "DNA Panel",
    items: [
      { label: "CEA", value: "Clear" },
      { label: "DM", value: "Clear" },
      { label: "SN", value: "Clear" },
      { label: "TNS", value: "Clear" },
      { label: "IGS", value: "Clear" },
      { label: "NCL5", value: "Clear" },
      { label: "G/G", value: "Clear" },
      { label: "DH", value: "Clear" },
      { label: "EOD", value: "Clear" }
    ]
  }
];

const competitionHighlights = [
  {
    sport: "Herding",
    icon: herdingIcon,
    groups: [
      {
        label: "Level",
        items: ["USBCHA: Pro-Novice"]
      },
      {
        label: "Major Results",
        items: ["High in Trial and High Combined AKC winner"]
      }
    ]
  },
  {
    sport: "Flyball",
    icon: flyballIcon,
    groups: [
      {
        label: "Major Events",
        items: ["2025 Canam Reg 1 team - 2nd place", "Obtained her ONYX in 2024"]
      },
      {
        label: "Performance",
        items: ["Personal best: 3.764", "3.8 average in start"]
      }
    ]
  },
  {
    sport: "Dock Diving",
    icon: dockDivingIcon,
    groups: [
      {
        label: "Personal Bests",
        items: ["Distance: 26ft 10in"]
      }
    ]
  },
  {
    sport: "Fast CAT",
    icon: fastCatIcon,
    groups: [
      {
        label: "Personal Best",
        items: ["28.74mph"]
      }
    ]
  },
  {
    sport: "Barn Hunt",
    icon: barnHuntIcon,
    groups: [
      {
        label: "Title",
        items: ["Novice earned in 2024"]
      }
    ]
  }
];

const venusLitters = [
  {
    title: "Venus x Rival (2027)",
    href: "/litters/venus-rival-2027",
    image: require("../../assets/img/dogs/litters/vr2027/announcement.jpg"),
    status: "Border Collie Litter"
  },
  {
    title: "Venus x Rip (2023)",
    href: "/litters/venus-rip-2023",
    image: require("../../assets/img/dogs/litters/vr2023/announcement.jpg"),
    status: "Border Collie Litter"
  },
  {
    title: "Venus x Hop Sing (2022)",
    href: "/litters/venus-hopsing-2022",
    image: require("../../assets/img/dogs/litters/vh2022/announcement.jpg"),
    status: "Sport Mix Litter"
  }
];

function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function VenusPage() {
  const hasVideo = Boolean(venusProfile.video && venusProfile.video.src);

  return (
    <>
      <SEO
        title="Venus | Border Collie Sport Dog"
        description="Meet Venus, a health-tested smooth coat border collie in Raleigh NC competing in flyball, herding, dock diving, Fast CAT, and barn hunt while producing sport dog puppies for Endgame Sport Dogs."
        path="/dogs/venus"
      />
      <DogPage
        headerBg="venus"
        pageClassName="dog-detail-page dog-venus-page"
      >
        <section className="dog-facts-wrap" aria-label="Venus at a glance">
          <Container>
            <DogFactsBar facts={venusProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading eyebrow={`${venusProfile.registeredName}`} title={`About ${venusProfile.name}`} />
                <p>{venusProfile.about}</p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Venus stacked in conformation" src={stackImage} />
                <figcaption>{venusProfile.stats}</figcaption>
              </figure>
            </div>

            <div className="dog-health-spec-panel site-bg-light-blue">
              <div className="dog-health-panel-heading">
                <h2>Health & Genetics</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-health-results">
                {healthGroups.map(group => (
                <section
                  className={`dog-health-spec-group${group.title === "DNA Panel" ? " dog-health-dna-group" : ""}`}
                  key={group.title}
                >
                  <h3>{group.title}</h3>
                  <dl>
                    {group.items.map(item => (
                      <div className="dog-health-result" key={`${group.title}-${item.label}`}>
                        <span className="dog-health-check" aria-hidden="true">✓</span>
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                  {group.note && <p>{group.note}</p>}
                </section>
                ))}
              </div>
              <section className="dog-health-spec-group dog-health-actions-group">
                <h3>Documents</h3>
                <DogProfileActions
                  dogName={venusProfile.name}
                  pedigreeImage={pedigreeImage}
                  pedigreeAlt="Venus pedigree"
                  clearances={clearances}
                  className="dog-profile-actions"
                />
              </section>
            </div>

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p>{venusProfile.temperament}</p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p>{venusProfile.athleticAbility}</p>
              </article>
            </div>
          </Container>
        </section>

        <div className="dog-performance-wrap">
          {hasVideo && (
            <section className="dog-detail-section dog-video-section">
              <Container>
                <DogSectionHeading eyebrow={`${venusProfile.name} in Action`} title={`Herding and Flyball clips`} />
                <div className="dog-video-feature">
                  <div className="dog-video-embed">
                    <h3>{venusProfile.video.title}</h3>
                    <div className="video-frame">
                      <iframe
                        src={venusProfile.video.src}
                        title={venusProfile.video.title}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                  <div className="dog-video-caption">
                    <p>Venus has done well at local herding trials. Videos of her herding can be found in the playlist to the left. She primarily competes in flyball averages a 3.9 in the pack and 3.8s in start. She has been focusing on flyball these days and is a reliable partner for any handler, even strangers. She's produced successful offspring in agility, flyball, dock diving, and disc but is most valued for her sweet nature and loving self.</p>
                  </div>
                </div>
              </Container>
            </section>
          )}

          <section className="dog-detail-section dog-competition-section">
            <Container>
              <div className="dog-health-panel-heading dog-competition-panel-heading">
                <h2>Competition Highlights</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-competition-grid">
                {competitionHighlights.map(group => (
                  <section className="dog-sport-highlight" key={group.sport}>
                    <h3>
                      <span className="dog-sport-highlight-icon" aria-hidden="true">
                        <img alt="" src={group.icon} />
                      </span>
                      {group.sport}
                    </h3>
                    {group.groups.map(section => (
                      <div className="dog-sport-highlight-group" key={`${group.sport}-${section.label}`}>
                        <h4>{section.label}</h4>
                        <ul>
                          {section.items.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </Container>
          </section>
        </div>

        <section className="dog-detail-section dog-litters-section">
          <Container>
            <DogSectionHeading title="Upcoming & Previous Litters" />
            <LitterListingGrid
              rowClassName="litter-gallery-grid dog-detail-litter-grid"
              cardClassName="litter-nameplate-card litter-gallery-card"
              litters={venusLitters}
            />
          </Container>
        </section>

        <section className="dog-detail-section dog-gallery-section site-bg-dark-navy">
          <Container>
            <DogSectionHeading title={`Photo Gallery`} />
          </Container>
          <DogGallery images={galleryImages} />
        </section>
      </DogPage>
    </>
  );
}

export default VenusPage;
